import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { useSelector } from "react-redux";
import { getUserData } from "src/redux/selectors/user";
import { ClientSelectionModal } from "src/pages/TicketForm/CreateForm/components/ClientSelectionModal";
import { ParentWorkOrderSelectionModal } from "src/pages/TicketForm/CreateForm/components/ParentWorkOrderSelectionModal";
import { RelatedResourceSelectionModal } from "src/pages/TicketForm/CreateForm/components/RelatedResourceSelectionModal";
import {
  FirebaseDataService,
  PurchaseOrderInput,
  PurchaseOrderLineItem,
  PurchaseOrderRecord,
  PurchasingDocumentType,
} from "src/services/Firebase/data";
import { colors } from "src/styles";
import { Client, DBLocation, DBVendor, Ticket } from "src/types";
import { generateAndDownloadPurchaseOrderPdf } from "src/utils/pdfGenerator";

interface PurchaseOrderFormProps {
  initialData?: Partial<PurchaseOrderInput>;
  purchaseOrderId?: string;
  onCancel?: () => void;
  onSaved?: (purchaseOrder: PurchaseOrderRecord) => void;
  submitText?: string;
  enableClientLocationSelection?: boolean;
}

const normalizeToArray = <T extends Record<string, any>>(input: unknown): T[] => {
  if (Array.isArray(input)) {
    return input.filter(Boolean) as T[];
  }

  if (input && typeof input === "object") {
    return Object.entries(input as Record<string, any>).map(([id, value]) => ({
      ...(value || {}),
      id: String((value as any)?.id || id || ""),
    })) as T[];
  }

  return [];
};

const EMPTY_LINE: PurchaseOrderLineItem = {
  id: uuidv4(),
  description: "",
  serviceType: "",
  sku: "",
  quantity: 1,
  unitOfMeasure: "EA",
  unitPrice: 0,
  lineTotal: 0,
};

const getDocumentTypeLabel = (type: PurchasingDocumentType) => {
  if (type === "blanket_purchase_order") return "Blanket Purchase Order";
  if (type === "purchase_requisition") return "Purchase Requisition";
  return "Purchase Order";
};

const getDocumentTypePrefix = (type: PurchasingDocumentType) => {
  if (type === "blanket_purchase_order") return "BPO";
  if (type === "purchase_requisition") return "PR";
  return "PO";
};

const normalizeDocumentType = (rawType: unknown): PurchasingDocumentType => {
  const normalized = String(rawType || "")
    .trim()
    .toLowerCase();

  if (normalized === "blanket_purchase_order" || normalized === "blanket purchase order" || normalized === "bpo") {
    return "blanket_purchase_order";
  }

  if (normalized === "purchase_requisition" || normalized === "purchase requisition" || normalized === "pr") {
    return "purchase_requisition";
  }

  return "purchase_order";
};

const generatePurchaseDocumentNumber = (type: PurchasingDocumentType) => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const suffix = `${Math.floor(1000 + Math.random() * 9000)}`;
  return `${getDocumentTypePrefix(type)}-${yyyy}${mm}${dd}-${suffix}`;
};

const PurchaseOrderForm = ({
  initialData,
  purchaseOrderId,
  onCancel,
  onSaved,
  submitText,
  enableClientLocationSelection = false,
}: PurchaseOrderFormProps) => {
  const {
    id: clientUserId,
    isManager,
    isDispatch,
    role,
    firstName,
    lastName,
    phoneNumber,
    email,
  } = useSelector(getUserData);
  const initialDocumentType = normalizeDocumentType(initialData?.documentType);

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isClientLocationLoading, setIsClientLocationLoading] = useState(false);
  const [isParentWorkOrderModalOpen, setIsParentWorkOrderModalOpen] = useState(false);
  const [isWorkOrdersLoading, setIsWorkOrdersLoading] = useState(false);
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
  const [isVendorsLoading, setIsVendorsLoading] = useState(false);

  const [clients, setClients] = useState<Client[]>([]);
  const [locations, setLocations] = useState<DBLocation[]>([]);
  const [workOrders, setWorkOrders] = useState<Ticket[]>([]);
  const [vendors, setVendors] = useState<DBVendor[]>([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState("");

  const [documentType, setDocumentType] = useState<PurchasingDocumentType>(initialDocumentType);
  const [poDescription, setPoDescription] = useState(initialData?.description || "");
  const [poNumber, setPoNumber] = useState(initialData?.poNumber || generatePurchaseDocumentNumber(documentType));
  const [poDate, setPoDate] = useState(initialData?.poDate?.slice(0, 10) || new Date().toISOString().slice(0, 10));
  const [status, setStatus] = useState<PurchaseOrderInput["status"]>(initialData?.status || "draft");
  const [linkedWorkOrderId, setLinkedWorkOrderId] = useState(String(initialData?.linkedWorkOrderId || ""));
  const [linkedWonum, setLinkedWonum] = useState(initialData?.linkedWonum || "");

  const [companyName, setCompanyName] = useState(initialData?.companyName || "");
  const [billingAddress, setBillingAddress] = useState(initialData?.billingAddress || "");
  const [shippingAddress, setShippingAddress] = useState(initialData?.shippingAddress || "");

  const [contactName, setContactName] = useState(
    initialData?.contact?.name || `${firstName || ""} ${lastName || ""}`.trim(),
  );
  const [contactPhone, setContactPhone] = useState(initialData?.contact?.phone || phoneNumber || "");
  const [contactEmail, setContactEmail] = useState(initialData?.contact?.email || email || "");

  const [vendorName, setVendorName] = useState(initialData?.vendorName || "");
  const [vendorAddress, setVendorAddress] = useState(initialData?.vendorAddress || "");
  const [vendorContactInfo, setVendorContactInfo] = useState(initialData?.vendorContactInfo || "");
  const [vendorId, setVendorId] = useState(initialData?.vendorId || "");

  const [lineItems, setLineItems] = useState<PurchaseOrderLineItem[]>(
    initialData?.lineItems?.length
      ? initialData.lineItems.map((item) => ({
          ...item,
          id: item.id || uuidv4(),
          quantity: Number(item.quantity || 0),
          unitPrice: Number(item.unitPrice || 0),
          lineTotal: Number(item.lineTotal ?? Number(item.quantity || 0) * Number(item.unitPrice || 0)),
        }))
      : [EMPTY_LINE],
  );

  const [tax, setTax] = useState<number>(Number(initialData?.tax || 0));
  const [shippingCost, setShippingCost] = useState<number>(Number(initialData?.shippingCost || 0));

  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState(
    initialData?.requestedDeliveryDate?.slice(0, 10) || "",
  );
  const [approverName, setApproverName] = useState(initialData?.approverName || "");
  const [notes, setNotes] = useState(initialData?.notes || "");

  const isEditMode = Boolean(purchaseOrderId);

  useEffect(() => {
    if (!isEditMode) {
      setPoNumber((currentValue) => {
        if (!currentValue) {
          return generatePurchaseDocumentNumber(documentType);
        }

        const expectedPrefix = `${getDocumentTypePrefix(documentType)}-`;
        return currentValue.startsWith(expectedPrefix) ? currentValue : generatePurchaseDocumentNumber(documentType);
      });
      setPoDate(new Date().toISOString().slice(0, 10));
    }
  }, [documentType, isEditMode]);

  useEffect(() => {
    if (!enableClientLocationSelection) return;

    const loadClientAndLocationData = async () => {
      setIsClientLocationLoading(true);

      try {
        const [clientsResponse, locationsResponse] = await Promise.allSettled(
          isDispatch
            ? [
                FirebaseDataService.getEmployeeClients({ employeeId: clientUserId, role: role || "dispatch" }),
                FirebaseDataService.getLocations(),
              ]
            : [
                FirebaseDataService.getClientsByManagerId({ managerId: clientUserId }),
                FirebaseDataService.getLocationsByManagerId({ managerId: clientUserId }),
              ],
        );

        const rawClients = clientsResponse.status === "fulfilled" ? clientsResponse.value.data : [];
        const rawLocations = locationsResponse.status === "fulfilled" ? locationsResponse.value.data : [];

        setClients(normalizeToArray<Client>(rawClients));
        setLocations(normalizeToArray<DBLocation>(rawLocations));
      } finally {
        setIsClientLocationLoading(false);
      }
    };

    void loadClientAndLocationData();
  }, [clientUserId, enableClientLocationSelection, isDispatch, role]);

  useEffect(() => {
    const loadWorkOrders = async () => {
      setIsWorkOrdersLoading(true);
      try {
        const response = await FirebaseDataService.getWorkOrders(clientUserId, isDispatch ? true : !!isManager);
        const nextWorkOrders = Array.isArray(response?.data?.workOrders) ? response.data.workOrders : [];
        setWorkOrders(nextWorkOrders);
      } catch {
        setWorkOrders([]);
      } finally {
        setIsWorkOrdersLoading(false);
      }
    };

    void loadWorkOrders();
  }, [clientUserId, isManager, isDispatch]);

  useEffect(() => {
    const loadVendors = async () => {
      setIsVendorsLoading(true);

      try {
        const response = await FirebaseDataService.getEmployeeVendors(clientUserId);
        setVendors(normalizeToArray<DBVendor>(response?.data));
      } catch {
        setVendors([]);
      } finally {
        setIsVendorsLoading(false);
      }
    };

    void loadVendors();
  }, [clientUserId]);

  const selectedClientRecord = useMemo(
    () => clients.find((client) => String(client.id) === String(selectedClientId)),
    [clients, selectedClientId],
  );

  const selectedLocationRecord = useMemo(
    () => locations.find((location) => String(location.id) === String(selectedLocationId)),
    [locations, selectedLocationId],
  );

  const selectedClientName = selectedClientRecord?.name || "";
  const selectedLocationLabel = selectedLocationRecord?.label || "";

  const selectedVendorRecord = useMemo(
    () => vendors.find((vendor) => String(vendor.id) === String(vendorId)),
    [vendors, vendorId],
  );

  const parentWorkOrderOptions = useMemo(() => {
    if (!selectedClientId) return workOrders;

    return workOrders.filter((workOrder) => {
      if (!workOrder.clientId) return true;
      return String(workOrder.clientId) === String(selectedClientId);
    });
  }, [workOrders, selectedClientId]);

  const selectedLinkedWorkOrder = useMemo(
    () => parentWorkOrderOptions.find((workOrder) => workOrder.wonum === linkedWonum),
    [parentWorkOrderOptions, linkedWonum],
  );

  useEffect(() => {
    if (!linkedWonum || linkedWorkOrderId) return;
    const matchedWorkOrder = workOrders.find((workOrder) => workOrder.wonum === linkedWonum);
    if (matchedWorkOrder?.workorderid) {
      setLinkedWorkOrderId(String(matchedWorkOrder.workorderid));
    }
  }, [workOrders, linkedWonum, linkedWorkOrderId]);

  const vendorModalOptions = useMemo(
    () =>
      vendors.map((vendor) => ({
        id: String(vendor.id || ""),
        name: vendor.name || "Unnamed",
        details: vendor.address || "No address on file",
      })),
    [vendors],
  );

  const subtotal = useMemo(() => lineItems.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0), [lineItems]);

  const totalAmount = useMemo(
    () => subtotal + Number(tax || 0) + Number(shippingCost || 0),
    [subtotal, tax, shippingCost],
  );

  const handleClientModalConfirm = (nextClientId: string, nextLocationId: string) => {
    const normalizedClientId = String(nextClientId || "");
    const normalizedLocationId = String(nextLocationId || "");
    const clientRecord = clients.find((client) => String(client.id) === normalizedClientId);
    const locationRecord = locations.find((location) => String(location.id) === normalizedLocationId);
    const locationContact = Object.values(locationRecord?.contacts || {})[0];
    const fallbackClientContactName = [clientRecord?.firstName, clientRecord?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const locationContactName = [locationContact?.firstName, locationContact?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();

    setSelectedClientId(normalizedClientId);
    setSelectedLocationId(normalizedLocationId);

    setCompanyName(clientRecord?.name || "");
    setBillingAddress(clientRecord?.address || "");
    setShippingAddress(locationRecord?.address || locationRecord?.label || "");
    setContactName(locationContactName || fallbackClientContactName || "");
    setContactPhone(locationContact?.phone || clientRecord?.phone || "");
    setContactEmail(locationContact?.email || clientRecord?.email || "");

    setIsClientModalOpen(false);
  };

  const handleVendorModalConfirm = async (nextVendorId: string) => {
    const normalizedVendorId = String(nextVendorId || "");
    const vendorRecord = vendors.find((vendor) => String(vendor.id) === normalizedVendorId);

    setVendorId(normalizedVendorId);
    setVendorName(vendorRecord?.name || "");
    setVendorAddress(vendorRecord?.address || "");

    const quickContactInfo = [
      String((vendorRecord as any)?.phone || "").trim(),
      String((vendorRecord as any)?.email || "").trim(),
    ]
      .filter(Boolean)
      .join(" | ");

    setVendorContactInfo(quickContactInfo);
    setIsVendorModalOpen(false);

    if (!normalizedVendorId) {
      return;
    }

    try {
      const vendorDetailsResponse = await FirebaseDataService.getVendorDetails(normalizedVendorId);
      const vendorDetails = vendorDetailsResponse?.data?.vendorDetails;

      const detailedAddress = [
        vendorDetails?.address?.addressLine1,
        vendorDetails?.address?.city,
        vendorDetails?.address?.state,
        vendorDetails?.address?.postalCode,
      ]
        .filter(Boolean)
        .join(", ");

      const primaryContact = Object.values(vendorDetails?.contacts || {})[0] as any;
      const detailedContact = [
        String(primaryContact?.name || "").trim(),
        String(primaryContact?.phone || vendorDetails?.phone || "").trim(),
        String(primaryContact?.email || "").trim(),
      ]
        .filter(Boolean)
        .join(" | ");

      if (vendorDetails?.name) {
        setVendorName(vendorDetails.name);
      }
      if (detailedAddress) {
        setVendorAddress(detailedAddress);
      }
      if (detailedContact) {
        setVendorContactInfo(detailedContact);
      }
    } catch {
      // Keep quick-fill values if vendor details lookup fails.
    }
  };

  const updateLineItem = (
    id: string,
    field: keyof Omit<PurchaseOrderLineItem, "id" | "lineTotal">,
    value: string | number,
  ) => {
    setLineItems((items) =>
      items.map((item) => {
        if (item.id !== id) return item;

        const nextItem = {
          ...item,
          [field]: value,
        };

        const quantity = Number(nextItem.quantity || 0);
        const unitPrice = Number(nextItem.unitPrice || 0);

        return {
          ...nextItem,
          quantity,
          unitPrice,
          lineTotal: quantity * unitPrice,
        };
      }),
    );
  };

  const handleNumericInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (event.target.value === "0") {
      event.target.select();
    }
  };

  const addLineItem = () => {
    setLineItems((items) => [...items, { ...EMPTY_LINE, id: uuidv4() }]);
  };

  const removeLineItem = (id: string) => {
    setLineItems((items) => {
      const next = items.filter((item) => item.id !== id);
      return next.length ? next : [{ ...EMPTY_LINE, id: uuidv4() }];
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    if (!companyName.trim()) {
      setError("Company name is required.");
      return;
    }

    if (!vendorName.trim()) {
      setError("Vendor name is required.");
      return;
    }

    if (!lineItems.some((item) => item.description.trim())) {
      setError("At least one line item with a description is required.");
      return;
    }

    setIsSaving(true);

    try {
      const payload: Partial<PurchaseOrderInput> = {
        documentType,
        description: poDescription.trim(),
        poNumber: poNumber.trim(),
        poDate,
        status,
        companyName: companyName.trim(),
        billingAddress: billingAddress.trim(),
        shippingAddress: shippingAddress.trim(),
        contact: {
          name: contactName.trim(),
          phone: contactPhone.trim(),
          email: contactEmail.trim(),
        },
        vendorName: vendorName.trim(),
        vendorAddress: vendorAddress.trim(),
        vendorContactInfo: vendorContactInfo.trim(),
        vendorId: vendorId.trim(),
        lineItems: lineItems.map((item) => ({
          ...item,
          description: item.description.trim(),
          serviceType: String(item.serviceType || "").trim(),
          sku: item.sku.trim(),
          unitOfMeasure: item.unitOfMeasure.trim() || "EA",
          quantity: Number(item.quantity || 0),
          unitPrice: Number(item.unitPrice || 0),
          lineTotal: Number(item.lineTotal || 0),
        })),
        subtotal,
        tax: Number(tax || 0),
        shippingCost: Number(shippingCost || 0),
        totalAmount,
        requestedDeliveryDate,
        approverName: approverName.trim(),
        notes: notes.trim(),
        linkedWorkOrderId: initialData?.linkedWorkOrderId || "",
        linkedWonum: linkedWonum.trim(),
        source: initialData?.source || "purchasing_tab",
      };

      payload.linkedWorkOrderId = linkedWorkOrderId.trim() || payload.linkedWorkOrderId || "";

      if (isEditMode && purchaseOrderId) {
        await FirebaseDataService.editPurchaseOrder(purchaseOrderId, payload);
        onSaved?.({ ...(initialData as PurchaseOrderRecord), ...payload } as PurchaseOrderRecord);
      } else {
        const { data } = await FirebaseDataService.createPurchaseOrder(clientUserId, !!isManager, payload);
        await generateAndDownloadPurchaseOrderPdf(
          data,
          `${data.documentType === "purchase_requisition" ? "PR" : "PO"}_${data.poNumber}`,
        );

        onSaved?.(data);
      }
    } catch (saveError: any) {
      setError(
        saveError?.message ||
          `Unable to ${isEditMode ? "save purchase document changes" : "create purchase document"}.`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormRoot onSubmit={handleSubmit}>
      <PageHeaderBlock>
        <PageEyebrow>Work</PageEyebrow>
        <PageTitle>{isEditMode ? "Edit Purchase Document" : "Create Purchase Document"}</PageTitle>
        <PageMeta>
          {getDocumentTypeLabel(documentType)} · {poNumber || "Auto PO #"}
        </PageMeta>
      </PageHeaderBlock>

      <ViewSectionRow>
        <ViewSectionMeta>
          <ViewSectionIndex>01</ViewSectionIndex>
          <ViewSectionTitle>Document</ViewSectionTitle>
        </ViewSectionMeta>
        <ViewSectionBody>
          <ViewFieldGrid>
            <ViewField>
              <ViewFieldLabel>Type</ViewFieldLabel>
              <ViewFieldSelect
                $isEditActive
                value={documentType}
                onChange={(event) => setDocumentType(event.target.value as PurchasingDocumentType)}
              >
                <option value="blanket_purchase_order">Blanket Purchase Order</option>
                <option value="purchase_order">Purchase Order</option>
                <option value="purchase_requisition">Purchase Requisition</option>
              </ViewFieldSelect>
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Description</ViewFieldLabel>
              <ViewFieldInput
                $isEditActive
                value={poDescription}
                onChange={(event) => setPoDescription(event.target.value)}
                maxLength={200}
                placeholder="Short description"
              />
            </ViewField>
            <ViewField>
              <ViewFieldLabel>PO Number</ViewFieldLabel>
              <ViewFieldInput disabled value={poNumber} readOnly placeholder="Auto-generated" />
            </ViewField>
            <ViewField>
              <ViewFieldLabel>PO Date</ViewFieldLabel>
              <ViewFieldInput disabled type="date" value={poDate} readOnly />
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Linked Work Order</ViewFieldLabel>
              <ParentWorkOrderButton
                type="button"
                onClick={() => setIsParentWorkOrderModalOpen(true)}
                disabled={isWorkOrdersLoading}
              >
                <ParentWorkOrderButtonText>
                  {isWorkOrdersLoading
                    ? "Loading work orders..."
                    : selectedLinkedWorkOrder
                      ? `${selectedLinkedWorkOrder.wonum}: ${selectedLinkedWorkOrder.description || selectedLinkedWorkOrder.name || "Untitled work order"}`
                      : linkedWonum
                        ? linkedWonum
                        : "Select Work Order..."}
                </ParentWorkOrderButtonText>
                <ParentWorkOrderButtonChevron>›</ParentWorkOrderButtonChevron>
              </ParentWorkOrderButton>
              {!!linkedWonum && (
                <ClearParentLink
                  type="button"
                  onClick={() => {
                    setLinkedWonum("");
                    setLinkedWorkOrderId("");
                  }}
                >
                  Clear linked work order
                </ClearParentLink>
              )}
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Status</ViewFieldLabel>
              <ViewFieldSelect
                $isEditActive
                value={status}
                onChange={(event) => setStatus(event.target.value as PurchaseOrderInput["status"])}
              >
                <option value="draft">Draft</option>
                <option value="approved">Approved</option>
                <option value="sent">Sent</option>
                <option value="closed">Closed</option>
              </ViewFieldSelect>
            </ViewField>
          </ViewFieldGrid>
        </ViewSectionBody>
      </ViewSectionRow>

      <ViewSectionRow>
        <ViewSectionMeta>
          <ViewSectionIndex>02</ViewSectionIndex>
          <ViewSectionTitle>Company And Contact</ViewSectionTitle>
        </ViewSectionMeta>
        <ViewSectionBody>
          <ViewFieldGrid>
            {enableClientLocationSelection && (
              <ViewField style={{ gridColumn: "1 / -1" }}>
                <ViewFieldLabel>Client And Location</ViewFieldLabel>
                <ParentWorkOrderButton
                  type="button"
                  onClick={() => setIsClientModalOpen(true)}
                  disabled={isClientLocationLoading}
                >
                  <ParentWorkOrderButtonText>
                    {!selectedClientId
                      ? isClientLocationLoading
                        ? "Loading clients and locations..."
                        : "Select Client & Location..."
                      : `${selectedClientName}${selectedLocationLabel ? ` · ${selectedLocationLabel}` : ""}`}
                  </ParentWorkOrderButtonText>
                  <ParentWorkOrderButtonChevron>›</ParentWorkOrderButtonChevron>
                </ParentWorkOrderButton>
              </ViewField>
            )}
            <ViewField>
              <ViewFieldLabel>Company Name</ViewFieldLabel>
              <ViewFieldInput
                $isEditActive
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
              />
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Billing Address</ViewFieldLabel>
              <ViewFieldTextArea
                $isEditActive
                value={billingAddress}
                onChange={(event) => setBillingAddress(event.target.value)}
              />
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Shipping Address</ViewFieldLabel>
              <ViewFieldTextArea
                $isEditActive
                value={shippingAddress}
                onChange={(event) => setShippingAddress(event.target.value)}
              />
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Contact Name</ViewFieldLabel>
              <ViewFieldInput
                $isEditActive
                value={contactName}
                onChange={(event) => setContactName(event.target.value)}
              />
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Contact Phone</ViewFieldLabel>
              <ViewFieldInput
                $isEditActive
                value={contactPhone}
                onChange={(event) => setContactPhone(event.target.value)}
              />
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Contact Email</ViewFieldLabel>
              <ViewFieldInput
                $isEditActive
                type="email"
                value={contactEmail}
                onChange={(event) => setContactEmail(event.target.value)}
              />
            </ViewField>
          </ViewFieldGrid>
        </ViewSectionBody>
      </ViewSectionRow>

      <ViewSectionRow>
        <ViewSectionMeta>
          <ViewSectionIndex>03</ViewSectionIndex>
          <ViewSectionTitle>Vendor Or Supplier</ViewSectionTitle>
        </ViewSectionMeta>
        <ViewSectionBody>
          <ViewFieldGrid>
            <ViewField style={{ gridColumn: "1 / -1" }}>
              <ViewFieldLabel>Vendor</ViewFieldLabel>
              <ParentWorkOrderButton
                type="button"
                onClick={() => setIsVendorModalOpen(true)}
                disabled={isVendorsLoading}
              >
                <ParentWorkOrderButtonText>
                  {!vendorId
                    ? isVendorsLoading
                      ? "Loading vendors..."
                      : "Select Vendor..."
                    : selectedVendorRecord?.name || vendorName || "Selected Vendor"}
                </ParentWorkOrderButtonText>
                <ParentWorkOrderButtonChevron>›</ParentWorkOrderButtonChevron>
              </ParentWorkOrderButton>
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Vendor Name</ViewFieldLabel>
              <ViewFieldInput
                $isEditActive
                value={vendorName}
                onChange={(event) => setVendorName(event.target.value)}
              />
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Vendor Address</ViewFieldLabel>
              <ViewFieldTextArea
                $isEditActive
                value={vendorAddress}
                onChange={(event) => setVendorAddress(event.target.value)}
              />
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Vendor Contact Info</ViewFieldLabel>
              <ViewFieldInput
                $isEditActive
                value={vendorContactInfo}
                onChange={(event) => setVendorContactInfo(event.target.value)}
              />
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Vendor ID</ViewFieldLabel>
              <ViewFieldInput $isEditActive value={vendorId} onChange={(event) => setVendorId(event.target.value)} />
            </ViewField>
          </ViewFieldGrid>
        </ViewSectionBody>
      </ViewSectionRow>

      <ViewSectionRow $fullWidth>
        <ViewSectionMeta>
          <ViewSectionIndex>04</ViewSectionIndex>
          <ViewSectionTitle>Order Details</ViewSectionTitle>
        </ViewSectionMeta>
        <ViewSectionBody>
          <LineItemsTableWrapper>
            <LineItemsTable>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Service Type</th>
                  <th>SKU</th>
                  <th>Qty</th>
                  <th>UOM</th>
                  <th>Unit Price</th>
                  <th>Line Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <ViewFieldInput
                        $isEditActive
                        value={item.description}
                        onChange={(event) => updateLineItem(item.id, "description", event.target.value)}
                      />
                    </td>
                    <td>
                      <ViewFieldInput
                        $isEditActive
                        value={String(item.serviceType || "")}
                        onChange={(event) => updateLineItem(item.id, "serviceType", event.target.value)}
                      />
                    </td>
                    <td>
                      <ViewFieldInput
                        $isEditActive
                        value={item.sku}
                        onChange={(event) => updateLineItem(item.id, "sku", event.target.value)}
                      />
                    </td>
                    <td>
                      <ViewFieldInput
                        $isEditActive
                        type="number"
                        min={0}
                        value={item.quantity}
                        onFocus={handleNumericInputFocus}
                        onChange={(event) => updateLineItem(item.id, "quantity", Number(event.target.value || 0))}
                      />
                    </td>
                    <td>
                      <ViewFieldInput
                        $isEditActive
                        value={item.unitOfMeasure}
                        onChange={(event) => updateLineItem(item.id, "unitOfMeasure", event.target.value)}
                      />
                    </td>
                    <td>
                      <ViewFieldInput
                        $isEditActive
                        type="number"
                        min={0}
                        step="0.01"
                        value={item.unitPrice}
                        onFocus={handleNumericInputFocus}
                        onChange={(event) => updateLineItem(item.id, "unitPrice", Number(event.target.value || 0))}
                      />
                    </td>
                    <td>
                      <LineTotal>${item.lineTotal.toFixed(2)}</LineTotal>
                    </td>
                    <td>
                      <LineActionButton type="button" onClick={() => removeLineItem(item.id)}>
                        Remove
                      </LineActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </LineItemsTable>
          </LineItemsTableWrapper>
          <LineItemActions>
            <LineActionPrimaryButton type="button" onClick={addLineItem}>
              Add Line Item
            </LineActionPrimaryButton>
          </LineItemActions>
        </ViewSectionBody>
      </ViewSectionRow>

      <ViewSectionRow>
        <ViewSectionMeta>
          <ViewSectionIndex>05</ViewSectionIndex>
          <ViewSectionTitle>Financials And Terms</ViewSectionTitle>
        </ViewSectionMeta>
        <ViewSectionBody>
          <ViewFieldGrid>
            <ViewField>
              <ViewFieldLabel>Subtotal</ViewFieldLabel>
              <ViewFieldInput disabled value={subtotal.toFixed(2)} />
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Tax</ViewFieldLabel>
              <ViewFieldInput
                $isEditActive
                type="number"
                min={0}
                step="0.01"
                value={tax}
                onChange={(event) => setTax(Number(event.target.value || 0))}
              />
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Shipping Or Freight Cost</ViewFieldLabel>
              <ViewFieldInput
                $isEditActive
                type="number"
                min={0}
                step="0.01"
                value={shippingCost}
                onChange={(event) => setShippingCost(Number(event.target.value || 0))}
              />
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Total Amount</ViewFieldLabel>
              <ViewFieldInput disabled value={totalAmount.toFixed(2)} />
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Requested Delivery Date</ViewFieldLabel>
              <ViewFieldInput
                $isEditActive
                type="date"
                value={requestedDeliveryDate}
                onChange={(event) => setRequestedDeliveryDate(event.target.value)}
              />
            </ViewField>
            <ViewField>
              <ViewFieldLabel>Approver Name</ViewFieldLabel>
              <ViewFieldInput
                $isEditActive
                value={approverName}
                onChange={(event) => setApproverName(event.target.value)}
              />
            </ViewField>
            <ViewField style={{ gridColumn: "1 / -1" }}>
              <ViewFieldLabel>Notes Or Special Instructions</ViewFieldLabel>
              <ViewFieldTextArea $isEditActive value={notes} onChange={(event) => setNotes(event.target.value)} />
            </ViewField>
          </ViewFieldGrid>
        </ViewSectionBody>
      </ViewSectionRow>

      {!!error && <ErrorText>{error}</ErrorText>}

      {enableClientLocationSelection && (
        <ClientSelectionModal
          isOpen={isClientModalOpen}
          onClose={() => setIsClientModalOpen(false)}
          onConfirm={handleClientModalConfirm}
          clients={clients.map((client) => ({
            id: String(client.id),
            name: client.name || "",
            customerId: String(client.customerId || ""),
          }))}
          locations={locations}
          currentClientId={selectedClientId}
          currentLocationId={selectedLocationId}
        />
      )}

      <ParentWorkOrderSelectionModal
        isOpen={isParentWorkOrderModalOpen}
        onClose={() => setIsParentWorkOrderModalOpen(false)}
        workOrders={parentWorkOrderOptions}
        selectedWonum={linkedWonum}
        onConfirm={(wonum) => {
          setLinkedWonum(wonum);
          const selectedWorkOrder = parentWorkOrderOptions.find((workOrder) => workOrder.wonum === wonum);
          setLinkedWorkOrderId(String(selectedWorkOrder?.workorderid || ""));
        }}
      />

      <RelatedResourceSelectionModal
        isOpen={isVendorModalOpen}
        onClose={() => setIsVendorModalOpen(false)}
        resourceType="vendor"
        resources={vendorModalOptions}
        selectedResourceId={vendorId}
        onConfirm={(selectedResourceId) => {
          void handleVendorModalConfirm(selectedResourceId);
        }}
      />

      <Actions>
        <ActionButton type="button" onClick={onCancel} disabled={isSaving}>
          Cancel
        </ActionButton>
        <ActionButton type="submit" $primary disabled={isSaving}>
          {isSaving ? "Saving..." : submitText || (isEditMode ? "Save Changes" : "Generate Purchase Order")}
        </ActionButton>
      </Actions>
    </FormRoot>
  );
};

const FormRoot = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const PageHeaderBlock = styled.div`
  margin-bottom: 0;
  padding-bottom: 20px;
  border-bottom: 1px solid ${colors.grey.light12};
`;

const PageEyebrow = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${colors.grey.default};
  margin-bottom: 6px;
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${colors.black.light2};
  margin: 0 0 6px;
`;

const PageMeta = styled.p`
  font-size: 12px;
  font-weight: 300;
  color: ${colors.grey.default};
  margin: 0;
`;

const ViewSectionRow = styled.section<{ $fullWidth?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $fullWidth }) => ($fullWidth ? "1fr" : "260px 1fr")};
  gap: 48px;
  padding: 32px 0;
  border-top: 1px solid ${colors.grey.light12};

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ViewSectionMeta = styled.div``;

const ViewSectionIndex = styled.div`
  font-size: 11px;
  color: ${colors.grey.default};
  letter-spacing: 0.04em;
`;

const ViewSectionTitle = styled.h2`
  margin: 8px 0 0;
  font-size: 20px;
  line-height: 1.25;
  color: ${colors.black.light2};
`;

const ViewSectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ViewField = styled.div``;

const ViewFieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const ViewFieldLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${colors.black.light2};
  font-weight: 600;
`;

const ViewFieldInput = styled.input<{ $isEditActive?: boolean }>`
  width: 100%;
  border: 1px solid ${({ $isEditActive }) => ($isEditActive ? colors.black.light2 : colors.grey.light12)};
  padding: 14px 16px;
  font-size: 14px;
  background: ${({ $isEditActive }) => ($isEditActive ? colors.white.default : "#f7f8fa")};
  color: ${colors.black.light2};

  &:disabled {
    color: ${colors.black.light2};
    opacity: 1;
    cursor: default;
  }
`;

const ViewFieldSelect = styled.select<{ $isEditActive?: boolean }>`
  width: 100%;
  border: 1px solid ${({ $isEditActive }) => ($isEditActive ? colors.black.light2 : colors.grey.light12)};
  padding: 14px 16px;
  font-size: 14px;
  background: ${({ $isEditActive }) => ($isEditActive ? colors.white.default : "#f7f8fa")};
  color: ${colors.black.light2};

  &:disabled {
    color: ${colors.black.light2};
    opacity: 1;
    cursor: default;
  }
`;

const ViewFieldTextArea = styled.textarea<{ $isEditActive?: boolean }>`
  width: 100%;
  min-height: 140px;
  border: 1px solid ${({ $isEditActive }) => ($isEditActive ? colors.black.light2 : colors.grey.light12)};
  padding: 14px 16px;
  font-size: 14px;
  resize: vertical;
  background: ${({ $isEditActive }) => ($isEditActive ? colors.white.default : "#f7f8fa")};
  color: ${colors.black.light2};

  &:disabled {
    color: ${colors.black.light2};
    opacity: 1;
    cursor: default;
  }
`;

const ParentWorkOrderButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid #c9d2dc;
  background: #ffffff;
  padding: 14px 16px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    background: #f5f7fa;
  }
`;

const ParentWorkOrderButtonText = styled.span`
  font-size: 14px;
  color: #1f2a37;
  text-align: left;
`;

const ParentWorkOrderButtonChevron = styled.span`
  color: #6f7782;
  font-size: 18px;
  line-height: 1;
`;

const ClearParentLink = styled.button`
  margin-top: 8px;
  border: none;
  background: transparent;
  color: #6f7782;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
`;

const LineItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid ${colors.grey.light12};

  th,
  td {
    border-top: 1px solid ${colors.grey.light12};
    padding: 10px;
    font-size: 12px;
    vertical-align: top;
  }

  th {
    background: #f7f8fa;
    color: ${colors.black.light2};
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  tr:first-child td {
    border-top: 1px solid ${colors.grey.light12};
  }

  input {
    min-width: 88px;
  }
`;

const LineItemsTableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const LineTotal = styled.strong`
  color: ${colors.black.light2};
`;

const LineActionButton = styled.button`
  border: 1px solid ${colors.grey.light12};
  background: ${colors.white.default};
  color: ${colors.black.light2};
  padding: 10px 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  cursor: pointer;
`;

const LineItemActions = styled.div`
  margin-top: 10px;
`;

const LineActionPrimaryButton = styled.button`
  border: none;
  background: ${colors.black.light2};
  color: ${colors.white.default};
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  cursor: pointer;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 20px;
  border-top: 1px solid ${colors.grey.light12};
`;

const ActionButton = styled.button<{ $primary?: boolean }>`
  border: ${({ $primary }) => ($primary ? "none" : `1px solid ${colors.grey.light12}`)};
  background: ${({ $primary }) => ($primary ? colors.black.light2 : colors.white.default)};
  color: ${({ $primary }) => ($primary ? colors.white.default : colors.black.light2)};
  padding: 10px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  margin: 10px 0;
  color: #d33636;
  font-size: 12px;
`;

export default PurchaseOrderForm;
